import { Box, Button, Divider, Stack, TextField, Typography } from "@mui/material"
import { IAdvisor } from "../../../types"
import { useState } from "react";

export const LinksTab = ({ advisor }: { advisor: IAdvisor }) => {

    const project = advisor.survey_link?.project;
    const organization = project?.organization;
    const experience = project?.experience;

    const customDomain = organization?.custom_domain_enabled && organization?.custom_domain ? `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${organization?.custom_domain}${process.env.REACT_APP_APP_PORT ? `:${process.env.REACT_APP_APP_PORT}` : ''}` : null;
    const standardDomain = `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${project?.tag}.${process.env.REACT_APP_APP_ORIGIN}`;

    const landingPageUrl = customDomain ? `${customDomain}/${project?.tag}` : `${process.env.REACT_APP_APP_PROTOCOL ?? "https"}://${standardDomain}/advise`;
    const [copiedFields, setCopiedFields] = useState<Record<string, boolean>>({});  

    // TODO: remove these once we have a proper dashboard url  
    const dashboardUrl = experience === "advisor" ? `${standardDomain}/advisor` : `${standardDomain}/e/${advisor.survey_link?.public_id}/expert/${advisor.public_id}`; 
    const onboardingUrl = `${landingPageUrl}/${advisor.public_id}/start`;
    const introCalendarUrl = `${standardDomain}/meet/${advisor.public_id}`;
    const demoCalendarUrl = `${standardDomain}/meet/${advisor.public_id}/demo`;
    const trialCalendarUrl = `${standardDomain}/meet/${advisor.public_id}/trial`;
    const coInvestUrl = `${dashboardUrl}/invest`;
    const referrerCodeLink = `${landingPageUrl}?ar=${advisor.referrer_code}`;

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedFields({ ...copiedFields, [field]: true });
        // Reset the copied state after 2 seconds
        setTimeout(() => {
            setCopiedFields(prev => ({ ...prev, [field]: false }));
        }, 2000);
    }

    const LinkRow = ({ label, url, copyKey }: { label: string; url: string; copyKey: string }) => (
        <Stack direction="row" spacing={2} alignItems="center">
            <TextField
                label={label}
                value={url}
                disabled
                fullWidth
                size="small"
                sx={{ 
                    '& .MuiInputBase-input': { 
                        fontSize: '0.875rem',
                        fontFamily: 'monospace'
                    }
                }}
            />
            <Button
                size="small"
                color={copiedFields[copyKey] ? "success" : "primary"}
                variant={copiedFields[copyKey] ? "contained" : "outlined"}
                onClick={() => handleCopy(url, copyKey)}
                sx={{ minWidth: 80, whiteSpace: 'nowrap' }}
            >
                {copiedFields[copyKey] ? "Copied!" : "Copy"}
            </Button>
        </Stack>
    );

    return (
        <Box>
            <Stack spacing={3}>
                {/* Onboarding  */}
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        🚀 Onboarding & Activation
                    </Typography>
                    <Stack spacing={2}>
                        <LinkRow 
                            label="Onboarding URL" 
                            url={experience === "advisor" ? onboardingUrl : dashboardUrl} 
                            copyKey="onboarding" 
                        />
                        <LinkRow 
                            label="Survey URL" 
                            url={advisor.latest_survey_url ?? ''} 
                            copyKey="survey" 
                        />
                    </Stack>
                </Box>

                <Divider />

                {/* Calendar & Scheduling */}
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        📅 Calendars / Scheduling
                    </Typography>
                    <Stack spacing={2}>
                        <LinkRow 
                            label="Intro Calendar" 
                            url={introCalendarUrl} 
                            copyKey="intro_calendar" 
                        />
                        <LinkRow 
                            label="Demo Calendar" 
                            url={demoCalendarUrl} 
                            copyKey="demo_calendar" 
                        />
                        <LinkRow 
                            label="Trial Calendar" 
                            url={trialCalendarUrl} 
                            copyKey="trial_calendar" 
                        />
                    </Stack>
                </Box>

                <Divider />

                {/* Dashboard & Tools */}
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        💼 Advisor Dashboard
                    </Typography>
                    <Stack spacing={2}>
                        <LinkRow 
                            label="Home" 
                            url={dashboardUrl} 
                            copyKey="dashboard" 
                        />
                        <LinkRow 
                            label="Feedback" 
                            url={experience === "advisor" ? `${dashboardUrl}/feedback` : ""} 
                            copyKey="feedback" 
                        />
                        <LinkRow 
                            label="Meet" 
                            url={`${dashboardUrl}/${experience === "advisor" ? "meet" : "engage"}`} 
                            copyKey="meet" 
                        />
                        <LinkRow 
                            label="Refer" 
                            url={`${dashboardUrl}/refer`} 
                            copyKey="refer" 
                        />
                        <LinkRow 
                            label="Shares" 
                            url={experience === "advisor" ? `${dashboardUrl}/shares` : ""} 
                            copyKey="shares" 
                        />
                        <LinkRow 
                            label="Invest" 
                            url={coInvestUrl} 
                            copyKey="invest" 
                        />
                    </Stack>
                </Box>

                <Divider />

                {/* Referrals */}
                <Box>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        🤝 Referral
                    </Typography>
                    <Stack spacing={2}>
                        <LinkRow 
                            label="Referrer Link" 
                            url={referrerCodeLink} 
                            copyKey="referrer" 
                        />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )
}